import { IPhotoPrivacyAndTerms, IPolicyTerm } from "@app-rest/common.interfaces";
import { IResponseError } from "@app-rest/index";
import { ICommonParts, IFunctionalities, IGenericPage, IHomePage, INavigationBar, IOpeningTimes, IPlayersTypes, IReservationPageResponse, IStatusFoodAndBeverageAvailableAt, ISystemConfigurations, ISystemCurrency, ISystemInfo, ITerms, IThankYouPageResponse, ProviderConfiguration } from "@app-rest/systems.interfaces";
import { dateKey, dateTimeKey } from "@app-utilities/dates";
import { SystemConfigurationName } from "@app-utilities/interfaces";
import { useCloudBackend } from "@qamf/shell-app-sdk";

export type PageType = "FOOD AND DRINK" | "GAMES AND EXTRAS" | "REGISTER" | "SHOES AND SOCKS" | "WEB OFFERS" | "YOUR ORDER" | "RESERVATION ON HOLD" | "RESERVATION SUBMITTED" | "RESERVATION CONFIRMED" | "THANK YOU ON HOLD" | "THANK YOU SUBMITTED" | "THANK YOU CONFIRMED";
const ProviderConfigurationErrors = ["BowlerProviderSetupNotFound"] as const;
type ProviderConfigurationErrorCodes = typeof ProviderConfigurationErrors[number];

export default function useSystemRestClient() {
	const { get } = useCloudBackend();

	function functionalities(systemId: number) {
		return get<IFunctionalities, IResponseError>(`/centers/${systemId}/functionalities`, {
			cacheInMemory: false,
			throwExcluding: [{ statusCode: 404 }]
		});
	}

	function configurations(systemId: number, Configurations: SystemConfigurationName[]) {
		return get<ISystemConfigurations>(`/centers/${systemId}/configuration`, {
			cacheInMemory: false,
			query: { Configurations }
		});
	}

	function info(systemId: number) {
		return get<ISystemInfo, IResponseError>(`/centers/${systemId}/summary`, {
			cacheInMemory: false,
			throwExcluding: [error => {
				if (error.statusCode === 401)
					return true;

				return ((error.statusCode === 404)
					&& typeof error.data?.Error?.Code === "string"
					&& error.data.Error.Code === "CenterNotFound");
			}]
		});
	}

	function playerTypes(systemId: number) {
		return get<IPlayersTypes>(`/centers/${systemId}/player-types`, { cacheInMemory: false });
	}

	function openingTimes(systemId: number, from: Date, to: Date) {
		return get<IOpeningTimes>(`/centers/${systemId}/opening-times/bookforlater/range`, {
			cacheInMemory: false,
			query: {
				fromDate: dateKey(from),
				toDate: dateKey(to)
			}
		});
	}

	function statusFoodAndBeverageAvailableAt(systemId: number, date: Date) {
		return get<IStatusFoodAndBeverageAvailableAt>(`/centers/${systemId}/opening-times/foodandbeverage/at`, {
			cacheInMemory: false,
			query: {
				dateTime: dateTimeKey(date)
			}
		});
	}

	function mainCurrency(systemId: number) {
		return get<ISystemCurrency, IResponseError>(`/centers/${systemId}/main-currency`, {
			cacheInMemory: false,
			throwExcluding: [
				error => {
					return ((error.statusCode === 404)
					&& typeof error.data?.Error?.Code === "string"
					&& error.data?.Error?.Code === "MainCurrencyNotPresent");
				}
			]
		});
	}

	function templateGenericPage(systemId: number, pageType: PageType) {
		return get<IGenericPage>(`/centers/${systemId}/page/page/${pageType}`, { cacheInMemory: true });
	}

	function templateCommonParts(systemId: number) {
		return get<ICommonParts>(`/centers/${systemId}/commonparts`, { cacheInMemory: true });
	}

	function templateHomePage(systemId: number) {
		return get<IHomePage>(`/centers/${systemId}/page/homepage`, { cacheInMemory: true });
	}

	function templateReservationPage(systemId: number) {
		return get<IReservationPageResponse>(`/centers/${systemId}/page/reservation`, { cacheInMemory: true });
	}

	function templateThankYouPage(systemId: number) {
		return get<IThankYouPageResponse>(`/centers/${systemId}/page/thank-you`, { cacheInMemory: true });
	}

	function templateCookiePage(systemId: number) {
		return get<IGenericPage>(`/centers/${systemId}/bowlers-template/page/cookie-policy`, { cacheInMemory: true });
	}

	function templateNavigationBar(systemId: number) {
		return get<INavigationBar[]>(`/centers/${systemId}/NavigationBar`, { cacheInMemory: true });
	}

	function getProviderConfiguration(systemId: number) {
		return get<ProviderConfiguration, IResponseError<ProviderConfigurationErrorCodes>>(`/centers/${systemId}/ProviderConfiguration`, {
			cacheInMemory: false,
			throwExcluding: [
				error => {
					return ((error.statusCode === 404)
					&& typeof error.data?.Error?.Code === "string"
					&& ProviderConfigurationErrors.includes(error.data.Error.Code));
				}]
		});
	}

	function getTermsList(systemId: number) {
		return get<ITerms>(`/centers/${systemId}/Terms`, { cacheInMemory: false });
	}

	function getTermsAndConditions(systemId: number) {
		return get<IPolicyTerm>(`/centers/${systemId}/Terms/BowlerBookForLaterTermsAndConditions`, { cacheInMemory: false });
	}

	function getTermsOfUse(systemId: number) {
		return get<IPolicyTerm>(`/centers/${systemId}/Terms/TermsOfUse`, { cacheInMemory: false });
	}

	function getPhotoPrivacyAndTerms(systemId: number) {
		return get<IPhotoPrivacyAndTerms>(`/${systemId}/PrivacyAndTerms`, { cacheInMemory: false });
	}

	function getMaintenanceModeStatus(systemId: number) {
		return get<boolean>(`/centers/${systemId}/MaintenanceMode`, { cacheInMemory: false });
	}

	return {
		functionalities,
		configurations,
		info,
		playerTypes,
		openingTimes,
		statusFoodAndBeverageAvailableAt,
		mainCurrency,
		templateGenericPage,
		templateCommonParts,
		templateHomePage,
		templateReservationPage,
		templateThankYouPage,
		templateCookiePage,
		templateNavigationBar,
		getProviderConfiguration,
		getTermsList,
		getTermsAndConditions,
		getTermsOfUse,
		getPhotoPrivacyAndTerms,
		getMaintenanceModeStatus
	};
}
